import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";

const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};

const Avatar = ({ member }) => {
  const photo = resolveMediaUrl(member.photo_url);
  if (photo) {
    return (
      <img
        src={photo}
        alt={member.name}
        className="w-full h-full object-cover"
      />
    );
  }
  // Fallback: initials with navy/gold gradient
  return (
    <div
      className="w-full h-full flex items-center justify-center"
      style={{
        background:
          "radial-gradient(circle at 30% 30%, rgba(201,169,97,0.35) 0%, rgba(201,169,97,0) 55%), linear-gradient(135deg, #0D2F4C 0%, #0A243A 100%)",
      }}
    >
      <span
        className="font-serif text-white"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "3.25rem",
          letterSpacing: "-0.02em",
        }}
      >
        {member.initials || member.name?.[0] || "•"}
      </span>
    </div>
  );
};

export const Team = () => {
  const { t, lang } = useLanguage();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/team");
        if (!cancelled) setMembers(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && members.length === 0) return null;

  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative bg-[#F8F6EF] py-24 sm:py-32 overflow-hidden"
    >
      {/* Soft gold corner accent */}
      <div
        aria-hidden
        className="absolute top-0 right-0 w-1/2 h-1/3 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, rgba(201,169,97,0.10) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
        {/* Header */}
        <div className="flex flex-col items-start mb-16 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-[#C9A961]" />
            <span
              data-testid="team-overline"
              className="text-xs uppercase tracking-[0.3em] font-bold text-[#C9A961]"
            >
              {t.team.overline}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="team-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#0D2F4C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.team.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[#4B5563] text-base leading-relaxed"
          >
            {t.team.subtitle}
          </motion.p>
        </div>

        {/* Members grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
          {members.map((m, i) => (
            <motion.article
              key={m.id}
              data-testid={`team-card-${m.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: 0.1 + i * 0.12 }}
              className="group relative bg-white border border-[#0D2F4C]/10 hover:border-[#C9A961]/60 transition-colors duration-300"
            >
              {/* Photo / avatar */}
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#0D2F4C]">
                <Avatar member={m} />
                {/* Gold top bar */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#C9A961] via-[#D4B775] to-[#C9A961] z-10" />
              </div>

              {/* Body */}
              <div className="p-7 sm:p-8">
                <h3
                  className="font-serif text-2xl text-[#0D2F4C] mb-1.5 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid={`team-card-${m.id}-name`}
                >
                  {m.name}
                </h3>
                <p className="text-xs uppercase tracking-[0.18em] text-[#C9A961] font-semibold mb-5">
                  {lang === "fr" ? m.title_fr : m.title_en}
                </p>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  {lang === "fr" ? m.bio_fr : m.bio_en}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
